/**
 * Runtime plugin loader: dynamic-import each reconciled plugin and let it
 * self-register into the `pluginRegistry`.
 *
 * Each plugin specifier resolves to an absolute path under the store's
 * `node_modules`; we import it via a `file://` URL so the import works inside
 * `bun --compile` binaries (which bundle only the literal-string switches, but
 * can still execute a `file://` import against the unpacked store on disk).
 * Failures are caught per-plugin so one broken plugin never aborts the rest.
 */

import { pathToFileURL } from "node:url";
import { type PluginRegistry, pluginRegistry } from "@skaile/workspaces/plugin-registry";
import type { Logger } from "@skaile/workspaces/types";
import { resolvePluginEntry } from "./paths.js";
import { parseSpec } from "./spec.js";

/** Options for {@link loadPlugins}. */
export interface LoadOptions {
  log?: Logger;
}

/** Result of a load pass: which specs registered and which failed. */
export interface LoadResult {
  loaded: string[];
  failed: { spec: string; error: string }[];
}

/** The self-registration entrypoint a plugin module is expected to export. */
type PluginModule = { register?: (registry: PluginRegistry) => void };

/**
 * Load and register each plugin in `plugins` into `registry`.
 *
 * @param projectDir - Project root holding `.skaile/plugins/node_modules`.
 * @param plugins - The `skaile.yaml` `plugins:` list (npm specifiers).
 * @param registry - Registry to register into. Defaults to the process-wide `pluginRegistry`.
 * @param opts - Optional logger.
 * @returns Loaded specs + per-plugin failures (never throws).
 */
export async function loadPlugins(
  projectDir: string,
  plugins: string[],
  registry: PluginRegistry = pluginRegistry,
  opts: LoadOptions = {},
): Promise<LoadResult> {
  const loaded: string[] = [];
  const failed: { spec: string; error: string }[] = [];

  for (const spec of plugins) {
    try {
      const { name } = parseSpec(spec);
      const entry = resolvePluginEntry(projectDir, name);
      const mod = (await import(pathToFileURL(entry).href)) as PluginModule;
      if (typeof mod.register !== "function") {
        throw new Error(`plugin "${name}" does not export a register() function`);
      }
      mod.register(registry);
      loaded.push(spec);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      failed.push({ spec, error: `failed to load plugin "${spec}": ${message}` });
      opts.log?.warn(`plugin load failed: ${spec}`, { error: message });
    }
  }

  return { loaded, failed };
}
